import { AvailableFeatures } from "event-definitions"
import { Repeat } from "lib/@components/repeat"
import { getCategoryForFeature } from "lib/feature-category"
import { FeatureDescriptionLine } from "lib/feature-description-line"

export function getAvailableFeatures() {
    return AvailableFeatures.call({ features: [] })
        .filter(({ hideInList }) => !hideInList)
        .unique("id")
}

const features = {}
export const FEATURE_STATUS_INTERNAL_USE_ONLY = "Internal use only"
export const FEATURE_STATUS_READY_FOR_PROD = "Ready for production"
export const FEATURE_STATUS_READY_FOR_BETA = "Ready for BETA"
export const FEATURE_STATUS_NOT_READY = "Not ready"

/**
 * Registers a new feature with the given name and id.
 *
 * @param props - the properties of the feature
 * @param {int} props.numericalId - A numerical id for product/ops to associate with the feature (must be unique) - if
 *     hidden in list, should be 0
 * @param {string} props.name - The name of the feature.
 * @param {string} props.id - The id of the feature.
 * @param {string} props.description - The description of the feature, in HTML.
 * @param {boolean} props.readyForBeta - If the feature is ready for beta testing.
 * @param {boolean} props.readyForProduction - If the feature is ready for release on production.
 * @param {boolean} props.internalUseOnly - If the feature is meant only for internal use.
 * @param {boolean} props.hideInList - If the feature is to be hidden from selection.
 * @param {string} props.category - Optional feature category for the admin console.
 * @return {string} The id of the registered feature.
 */
export function registerFeature({
    numericalId,
    name,
    id,
    description,
    category,
    readyForBeta = false,
    readyForProduction = false,
    internalUseOnly = false,
    hideInList = false,
}) {
    if (!id) throw new Error("All features must have an id")
    if (features[id]) return id
    features[id] = true

    AvailableFeatures.handleOnce(({ features }) => {
        features.push({
            numericalId,
            name,
            demand: `feature-${id}`,
            id,
            available: id?.includes("-all-users") ? "All Users" : "No",
            description,
            status: getStatus(internalUseOnly, readyForProduction, readyForBeta),
            category: getCategoryForFeature({ id, numericalId, category }),
            hideInList,
        })
    })
    return id
}

export function getDemandForFeature(feature) {
    return `feature-${feature}`
}

function getStatus(internalUseOnly, readyForProduction, readyForBeta) {
    let status = ""
    if (internalUseOnly) {
        status = FEATURE_STATUS_INTERNAL_USE_ONLY
    } else if (readyForProduction) {
        status = FEATURE_STATUS_READY_FOR_PROD
    } else if (readyForBeta) {
        status = FEATURE_STATUS_READY_FOR_BETA
    } else {
        status = FEATURE_STATUS_NOT_READY
    }
    return status
}

export function makeDescriptionComponent(lines = []) {
    return function FeatureDescription() {
        return (
            <ul
                style={{
                    listStyleType: "'· '",
                    paddingLeft: ".5rem",
                    margin: 0,
                }}
            >
                <Repeat list={lines} item={<FeatureDescriptionLine />} keyFn={getDescriptionLineKey} />
            </ul>
        )
    }
}

function getDescriptionLineKey(item, index) {
    return `${index}-${item}`
}

/**
 * Registers a feature flag with a bullet-point description component.
 *
 * @param {Object} options
 * @param {number} options.numericalId - Unique numeric ID for internal tracking.
 * @param {string} options.id - Unique string ID of the feature.
 * @param {string} options.name - Display name of the feature.
 * @param {string[]} options.descriptionLines - Bullet point lines for description.
 * @param {boolean} [options.readyForBeta=false] - Feature is ready for beta.
 * @param {boolean} [options.readyForProduction=false] - Feature is production-ready.
 * @param {boolean} [options.internalUseOnly=false] - Feature is for internal users only.
 * @param {boolean} [options.hideInList=false] - Feature should be hidden in list UI.
 * @param {string} [options.category] - Optional feature category for the admin console.
 * @returns {string} The ID of the registered feature.
 */
export function registerFeatureWithDescription({
    numericalId,
    id,
    name,
    descriptionLines = [],
    readyForBeta = false,
    readyForProduction = false,
    internalUseOnly = false,
    hideInList = false,
    category,
}) {
    const Description = makeDescriptionComponent(descriptionLines)
    return registerFeature({
        numericalId,
        id,
        name,
        description: <Description $lazy />,
        readyForBeta,
        readyForProduction,
        internalUseOnly,
        hideInList,
        category,
    })
}
